"use client";

import { useState } from "react";
import { useToggleToast } from "@/lib/toast";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";

interface Props {
  heading: string;
  eyebrow?: string;
  showFollow?: boolean;
  description?: string;
}

export function ListingHeader({
  heading,
  eyebrow,
  showFollow = false,
  description,
}: Props) {
  const [following, setFollowing] = useState(false);
  const notify = useToggleToast();
  return (
    <header className="flex flex-col gap-4 pb-8" style={{ borderBottom: `2px solid ${EJHEALTH_COLORS.primary}` }}>
      {eyebrow && (
        <span
          style={{
            ...EJHEALTH_TYPE.eyebrowLg,
            color: EJHEALTH_COLORS.primary,
            letterSpacing: "0.24em",
          }}
        >
          {eyebrow}
        </span>
      )}
      {/* The follow control sits NEXT TO the title, not at the opposite end of
          the row: `justify-between` pushed it against the container's right
          edge, where it read as an unrelated toolbar button rather than an
          action on the thing named beside it. QA: "move the follow the next to
          the tag title". */}
      <div className="flex flex-wrap items-center gap-4">
        <h1
          style={{
            ...EJHEALTH_TYPE.h1Hero,
            color: EJHEALTH_COLORS.dark,
            fontSize: 36,
            lineHeight: "44px",
            letterSpacing: "-0.018em",
          }}
          className="lg:!text-[44px] lg:!leading-[52px]"
        >
          {heading}
        </h1>
        {showFollow && (
          <button
            type="button"
            // `next` outside the updater — StrictMode runs updaters twice, so
            // a toast raised inside one fires twice. QA: "add toast to
            // actions".
            onClick={() => {
              const next = !following;
              setFollowing(next);
              notify.follow(next);
            }}
            aria-pressed={following}
            // Resting colours on custom properties, not inline: an inline
            // declaration outranks every `hover:` utility. The button had
            // `transition-colors` and nothing to transition — QA: "add hover
            // effect to the buttons".
            className="inline-flex items-center gap-2 border-2 border-[color:var(--rest-border)] bg-[color:var(--rest-bg)] px-5 py-2.5 text-[color:var(--rest-fg)] transition-colors duration-150 hover:border-[color:var(--hover-bg)] hover:bg-[color:var(--hover-bg)] hover:text-[color:var(--hover-fg)] motion-reduce:transition-none"
            style={
              {
                ...EJHEALTH_TYPE.button,
                ["--rest-border" as string]: EJHEALTH_COLORS.primary,
                ["--rest-bg" as string]: following
                  ? EJHEALTH_COLORS.surface
                  : EJHEALTH_COLORS.primary,
                ["--rest-fg" as string]: following
                  ? EJHEALTH_COLORS.primary
                  : EJHEALTH_COLORS.textOnDark,
                ["--hover-bg" as string]: EJHEALTH_COLORS.primaryDark,
                ["--hover-fg" as string]: EJHEALTH_COLORS.textOnDark,
              } as React.CSSProperties
            }
          >
            {following ? "已關注" : "＋ 關注"}
          </button>
        )}
      </div>
      {description && (
        <p
          className="max-w-[680px]"
          style={{
            ...EJHEALTH_TYPE.body,
            color: EJHEALTH_COLORS.textBody,
            fontSize: 15,
            lineHeight: "26px",
          }}
        >
          {description}
        </p>
      )}
    </header>
  );
}
